import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useContracts } from "@/hooks/useContracts";
import { ContractFormValues } from "@/components/contracts/form/ContractFormSchema";
import { ReminderPeriod, ReminderFrequency } from "@/lib/types";
import { toast } from "sonner";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";

export function useFormSubmission(mode: "create" | "edit" = "create", contract?: any) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applyServiceChanges, setApplyServiceChanges] = useState<(() => Promise<void>) | null>(null);
  
  const { useCreateContractMutation, useUpdateContractMutation } = useContracts();
  const createContractMutation = useCreateContractMutation();
  const updateContractMutation = useUpdateContractMutation();
  
  const isPending = isSubmitting || createContractMutation.isPending || updateContractMutation.isPending;

  const onSubmit = useCallback(async (data: ContractFormValues, selectedContacts: string[], attachments: File[], selectedContractType?: any) => {
    try {
      setIsSubmitting(true);
      
      if (mode === "edit" && applyServiceChanges) {
        try {
          await applyServiceChanges();
        } catch (error) {
          console.error("Error applying service changes:", error);
          toast.error("Failed to save service changes. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }
      
      data.contactPersonIds = selectedContacts;
      
      const paymentFrequency = {
        interval: data.paymentFrequencyInterval,
        unit: data.paymentFrequencyUnit
      };

      const categoryId = data.categoryId && data.categoryId.trim() !== "" ? data.categoryId : null;

      const contractData = {
        supplierName: data.supplierName,
        organisationnumber: data.organisationnumber || "",
        importance: data.importance as 1 | 2 | 3 | 4 | 5,
        type: selectedContractType!,
        expectedCost: data.expectedCost,
        currencyId: data.currencyId,
        paymentFrequency,
        signedDate: data.signedDate,
        expiryDate: data.expiryDate,
        reminderPeriod: data.reminderPeriod as ReminderPeriod,
        reminderFrequency: data.reminderFrequency as ReminderFrequency,
        categoryId: categoryId,
        contactPersonIds: selectedContacts,
        attachments: attachments,
        use_services_cost: data.use_services_cost,
        paymentType: data.paymentType,
      };

      if (mode === "create") {
        const newContract = await createContractMutation.mutateAsync(contractData as any);
        toast.success("Contract created successfully");
        navigate("/contracts");
      } else if (mode === "edit" && contract) {
        if (contract.expectedCost !== data.expectedCost) {
          const { supabase } = await import('@/integrations/supabase/client');
          
          await supabase.from('contract_changes').insert({
            contract_id: contract.id,
            user_id: (await supabase.auth.getUser()).data.user?.id,
            change_type: 'update',
            field_name: 'expectedCost',
            old_value: contract.expectedCost,
            new_value: data.expectedCost,
            metadata: { 
              currencyId: data.currencyId,
              contractName: data.supplierName,
              isIncome: selectedContractType?.direction === 'incoming'
            }
          });
        }

        await updateContractMutation.mutateAsync({
          id: contract.id,
          formData: contractData as any,
          skipCostChangeTracking: true
        });
        toast.success("Contract updated successfully");
      }
    } catch (error) {
      console.error("Error saving contract:", error);
      toast.error("Error saving contract. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [applyServiceChanges, createContractMutation, mode, navigate, updateContractMutation, contract]);

  const handleServicesApplyCallback = useCallback((callback: () => Promise<void>) => {
    setApplyServiceChanges(() => callback);
  }, []);

  return {
    isSubmitting,
    isPending,
    onSubmit,
    handleServicesApplyCallback
  };
}
